import { useEffect, useState } from "react";
import { useOffline } from "../../offline/OfflineContext";
import type { RoleSnapshot } from "../../offline/model";
import { hasAccessRight } from "../auth/accessRights";
import { useAuth } from "../auth/AuthContext";
import { selectNavigationRoles, usableRoleSnapshot } from "./roleSnapshot";
import { useOwnerKey } from "./tenantSelection";
import { useMe } from "./useMe";

/** Snapshot read for an owner. */
interface StoredRoles {
  /** Owner key the snapshot was read for. */
  readonly owner: string;
  /** The usable snapshot, or `null`. */
  readonly snapshot: RoleSnapshot | null;
}

/**
 * Returns the roles that decide which navigation entries are visible: the roles from `GET /me`, or
 * while the server has not answered (app start without connection) the last known roles of the
 * same signed-in person and tenant on this device (F-05, ADR-0104). Snapshots of other accounts are
 * deleted when the account changes.
 *
 * This is no authorization decision: it only shows or hides navigation entries. The server checks
 * every request on its own, and screens keep handling missing data and a missing connection.
 *
 * @returns The roles, or `undefined` if they are not known.
 */
export function useNavigationRoles(): readonly string[] | undefined {
  const me = useMe();
  const { user } = useAuth();
  const owner = useOwnerKey();
  const { store, nowMs } = useOffline();
  const subject = user?.subject ?? null;
  const [stored, setStored] = useState<StoredRoles | null>(null);

  useEffect(() => {
    if (subject === null || owner === null) {
      return undefined;
    }

    let active = true;
    const apply = (snapshot: RoleSnapshot | null): void => {
      if (active) {
        setStored({ owner, snapshot: usableRoleSnapshot(snapshot, owner, nowMs()) });
      }
    };
    // The account changed: the role snapshots, leave accounts, crews and rosters of other accounts are
    // deleted; the kept leave accounts, crews and rosters are a convenience, so a failure there does not
    // stop the roles.
    Promise.all([
      store.retainRoleSnapshots(subject),
      store.retainLeaveAccounts(subject).catch(() => undefined),
      store.retainCrewCaches(subject).catch(() => undefined),
      store.retainRosterCaches(subject).catch(() => undefined),
    ])
      .then(() => store.getRoleSnapshot(owner))
      .then(apply, () => {
        apply(null);
      });
    const cleanup = (): void => {
      active = false;
    };

    return cleanup;
  }, [store, nowMs, subject, owner]);

  const snapshot = stored !== null && stored.owner === owner ? stored.snapshot : null;
  const roles = selectNavigationRoles({ fresh: me.data?.roles, error: me.error, snapshot });
  const keepsCrew = roles === undefined || hasAccessRight(roles, "ClockForOthers");

  // Names and states of other people stay on the device only while the last known roles allow clocking
  // for others; a delegation that ended or a removed role deletes the kept crew (T-170, ADR-0139).
  useEffect(() => {
    if (owner !== null && !keepsCrew) {
      store.deleteCrewCache(owner).catch(() => undefined);
    }
  }, [store, owner, keepsCrew]);

  return roles;
}
